import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subscription } from 'rxjs'
import { MagliveApiClient } from 'src/lib/api-client/maglive-api-client'
import { ConsoleLogger } from 'src/lib/console-logger'
import { ORDER_STATUS_LIST, PAYMENT_METHOD_LIST } from 'src/lib/constants'
import { Order, OrderHistoriesRequest } from 'src/lib/entity/order'
import * as encoding from 'encoding-japanese'
import { saveAs } from 'file-saver'
import { ToastrService } from 'ngx-toastr'

@Component({
  selector: 'maglive-admin-order-history-index',
  templateUrl: './order-history-index.component.html',
  styleUrls: [
    './order-history-index.component.scss',
    '../../../../pages/admin/startbootstrap-sb-admin-gh-pages/src/scss/styles.scss',
  ],
})
export class AdminOrderHistoryIndexComponent implements OnInit, OnDestroy {
  orderHistories: Order[] = []
  paymentMethodList = PAYMENT_METHOD_LIST
  orderStatusList = ORDER_STATUS_LIST
  private orderHistoriesSub: Subscription
  searchParams = {
    id: null,
    userId: null,
    paymentMethod: null,
    pointPlanSerialNumber: null,
    status: null,
    createdAtStart: null,
    createdAtEnd: null,
  }
  // 無限スクロール対象要素の親クラス名
  infiniteScrollParentSelector: string = '.admin-order-history-scroll-wrap'
  // 無限スクロールでデータを取りきったかどうかのフラグ
  private isAllDataLoaded: Boolean = false
  // データ取得中かどうかのフラグ
  isLoading: Boolean = false

  invalidCsvDl: Boolean;
  csvDateValidationError: Boolean = false
  dateRequiredError: Boolean = false

  private lastDocumentId: string = null

  constructor(
    private logger: ConsoleLogger,
    public changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.isLoading = true
    this.searchParams.id = this.route.snapshot.queryParamMap.get('id') || null
    this.searchParams.userId = this.route.snapshot.queryParamMap.get('userId') || null
    this.searchParams.paymentMethod = this.route.snapshot.queryParamMap.get('paymentMethod') || null
    this.searchParams.pointPlanSerialNumber = this.route.snapshot.queryParamMap.get('pointPlanSerialNumber') || null
    this.searchParams.status = this.route.snapshot.queryParamMap.get('status') || null
    this.searchParams.createdAtStart = this.route.snapshot.queryParamMap.get('createdAtStart') || null
    this.searchParams.createdAtEnd = this.route.snapshot.queryParamMap.get('createdAtEnd') || null
    const request: OrderHistoriesRequest = {
      ...this.searchParams,
      createdAtStart: this.searchParams.createdAtStart ? Date.parse(this.searchParams.createdAtStart) / 1000 : null,
      createdAtEnd: this.searchParams.createdAtEnd ? Date.parse(this.searchParams.createdAtEnd) / 1000 : null,
      lastDocumentId: null,
    }
    this.fetchOrderHistories(request)
  }

  ngOnDestroy(): void {
    if (this.orderHistoriesSub) {
      this.orderHistoriesSub.unsubscribe()
    }
  }

  /**
   * 検索条件に基づいた注文履歴一覧を取得する
   */
  execSearch(): void {
    this.logger.info(this.constructor.name, 'execSearch', 'searchParams', this.searchParams);
    this.isAllDataLoaded = false
    this.isLoading = true
    const request: OrderHistoriesRequest = {
      ...this.searchParams,
      createdAtStart: this.searchParams.createdAtStart ? Date.parse(this.searchParams.createdAtStart) / 1000 : null,
      createdAtEnd: this.searchParams.createdAtEnd ? Date.parse(this.searchParams.createdAtEnd) / 1000 : null,
      lastDocumentId: null,
    }
    this.fetchOrderHistories(request)
    // 強制的に再描画させる
    this.changeDetectorRef.detectChanges()
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        ...this.searchParams,
      },
    })
  }

  /**
   * スクロールイベントをトリガーする
   */
  onScroll() {
    if (this.isAllDataLoaded || this.isLoading) return
    const request: OrderHistoriesRequest = {
      ...this.searchParams,
      createdAtStart: this.searchParams.createdAtStart ? Date.parse(this.searchParams.createdAtStart) / 1000 : null,
      createdAtEnd: this.searchParams.createdAtEnd ? Date.parse(this.searchParams.createdAtEnd) / 1000 : null,
      lastDocumentId: this.lastDocumentId,
    }
    this.isLoading = true
    this.fetchOrderHistoriesForScroll(request)
    // 強制的に再描画させる
    this.changeDetectorRef.detectChanges()
  }

  /**
   * 注文履歴を取得する
   */
  private fetchOrderHistories(request: OrderHistoriesRequest): void {
    MagliveApiClient.fetchOrderHistories(request).subscribe(res => {
      this.logger.info(this.constructor.name, 'fetchOrderHistories', 'orderHistories', res)
      if (res !== 'NOT_FOUND') {
        this.orderHistories = res.items
        this.lastDocumentId = res.lastDocumentId
      } else {
        this.orderHistories = []
      }
      this.isLoading = false
    })
  }

  /**
   * スクロール時の注文履歴取得処理
   */
  private fetchOrderHistoriesForScroll(request: OrderHistoriesRequest): void {
    MagliveApiClient.fetchOrderHistories(request).subscribe(res => {
      this.logger.info(this.constructor.name, 'fetchOrderHistoriesForScroll', 'orderHistories', res)
      if (res !== 'NOT_FOUND' && res.items.length > 0) {
        if (this.orderHistories.length !== 0) {
          this.orderHistories = this.orderHistories.concat(res.items)
        } else {
          this.orderHistories = res.items
        }
        this.lastDocumentId = res.lastDocumentId
      } else {
        // 無限スクロールでこれ以上取れるデータがない
        this.isAllDataLoaded = true
      }
      this.isLoading = false
    })
  }

  exportOrderHistories() {
    if (!this.searchParams.createdAtEnd || !this.searchParams.createdAtStart) {
      this.dateRequiredError = true
      return
    } else this.dateRequiredError = false
    this.lastDocumentId = null
    //日付の差が 1 か月以上あるかどうかを検証
    var months
    const start = new Date(this.searchParams.createdAtStart)
    const end = new Date(this.searchParams.createdAtEnd)
    months = (start.getFullYear() - end.getFullYear()) * 12
    months -= start.getMonth()
    months += end.getMonth()
    if (start.getDate() > end.getDate()) months--
    months <= 0
      ? (this.csvDateValidationError = false)
      : (this.csvDateValidationError = true)

    const request: OrderHistoriesRequest = {
      ...this.searchParams,
      createdAtStart: this.searchParams.createdAtStart
        ? Date.parse(this.searchParams.createdAtStart) / 1000
        : null,
      createdAtEnd: this.searchParams.createdAtEnd
        ? Date.parse(this.searchParams.createdAtEnd) / 1000
        : null,
      lastDocumentId: null,
    }
    if (!this.csvDateValidationError) this.getCsvData(request)
  }

  getCsvData(request: OrderHistoriesRequest) {
    this.invalidCsvDl = true
    MagliveApiClient.exportOrderHistories(request).subscribe(
      (value) => {
        if (value === 'NOT_FOUND') {
          this.invalidCsvDl = false
          this.toastr.error('エラーが発生しました。')
          return
        }

        if(value === 'VALIDATION_ERROR') {
          this.invalidCsvDl = false
          this.toastr.error('出力条件が正しくありません。')
          return
        }

        if (!value.fileData.length) {
          this.invalidCsvDl = false
          this.toastr.error('注文履歴データが存在していません。')
          return
        }

        const fileName = value.fileName
        const fileData = value.fileData

        const unicodeList = []
        for (let i = 0; i < fileData.length; i++) {
          unicodeList.push(fileData.charCodeAt(i))
        }

        const shiftJisCodeList = encoding.convert(
          unicodeList,
          'SJIS',
          'UNICODE'
        )
        const unit8Array = new Uint8Array(shiftJisCodeList)

        const blob = new Blob([unit8Array], { type: 'text/csv' })

        saveAs(blob, fileName)
        this.invalidCsvDl = false
      },
      (error) => {
        this.invalidCsvDl = false
        console.log(error)
      }
    )
  }
}
